import { useEffect, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { colorVarOf, useCountUp } from './shared.js';
import type { DesignerKpi } from './shared.js';

interface BulletPanelProps {
  /** designer 分の per-person KPI 配列 (現役デザイナー全員) */
  data: DesignerKpi[];
  /** Team レベル tooltip 内訳 (省略可) */
  teamBreakdown?: { completed: number; inProgress: number };
  /** designer bar のクリックハンドラ (省略時 bar は非インタラクティブなまま) */
  onSelectDesigner?: (id: string) => void;
  /** 全体 bar のクリックハンドラ (省略時 bar は非インタラクティブなまま) */
  onSelectTeam?: () => void;
}

/**
 * デザイナー個人の bullet chart + 末尾にチーム全体の太い bullet を表示する panel。
 *
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business IDT KPI ページ左上の bullet panel。現役デザイナー + チーム合計の進捗を一覧で見せる
 * @graph-connects none
 */
export function BulletPanel({
  data,
  teamBreakdown,
  onSelectDesigner,
  onSelectTeam,
}: BulletPanelProps) {
  const [animateIn, setAnimateIn] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => {
      setAnimateIn(true);
    }, 80);
    return () => {
      clearTimeout(t);
    };
  }, []);
  return (
    <div className="flex flex-col gap-3.5">
      {data.map((d) => (
        <BulletRow key={d.id} d={d} animateIn={animateIn} onSelect={onSelectDesigner} />
      ))}
      <div className="mt-2 pt-4 border-t border-[var(--color-border)]">
        <TeamProgressRow
          data={data}
          animateIn={animateIn}
          breakdown={teamBreakdown}
          onSelect={onSelectTeam}
        />
      </div>
    </div>
  );
}

/** @graph-connects none */
function BulletRow({
  d,
  animateIn,
  onSelect,
}: {
  d: DesignerKpi;
  animateIn: boolean;
  onSelect?: (id: string) => void;
}) {
  const { t } = useTranslation();
  const [hover, setHover] = useState(false);
  const colorVar = colorVarOf(d.id);
  // 実績が総目標を超えたら max(実績, 目標) を全幅基準にし、総目標 bar を比例縮小して
  // 実績 bar が目標を超過する様子を見せる (過去月/Q の達成率>100% を可視化)。
  const denom = Math.max(d.actual, d.monthlyTarget) || 1;
  const targetBarPct = (d.monthlyTarget / denom) * 100;
  const actualPct = animateIn ? (d.actual / denom) * 100 : 0;
  const tickPct = (d.currentTarget / denom) * 100;
  // currentTarget=0 (期首・新規参画で as-of 目標未設定) は 0 除算で NaN% になるため 0% に落とす
  const pct = d.currentTarget > 0 ? Math.round((d.actual / d.currentTarget) * 100) : 0;
  const displayedPct = useCountUp(pct, animateIn, 1000, 100);
  const isAhead = pct >= 100;

  return (
    <div className="grid grid-cols-[56px_1fr_52px] items-center gap-3">
      <span
        className="inline-flex justify-center px-2.5 py-1 rounded-md text-[11px] font-medium tracking-wide select-none"
        style={{
          background: `color-mix(in srgb, var(${colorVar}) 16%, transparent)`,
          color: `var(${colorVar})`,
        }}
      >
        {d.id}
      </span>
      <div
        className="relative h-6 rounded-md"
        style={{ background: 'transparent' }}
        onMouseEnter={() => {
          setHover(true);
        }}
        onMouseLeave={() => {
          setHover(false);
        }}
      >
        {/* 総目標 bar (実績が超過したら比例縮小する)。超過領域は背景なし (カード色) */}
        <div
          className="absolute inset-y-0 left-0 rounded-md"
          style={{
            width: `${String(targetBarPct)}%`,
            background: `color-mix(in srgb, var(${colorVar}) 30%, transparent)`,
            transition: 'width 900ms cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        />
        <div
          className="absolute top-1/2 left-0 h-4 -translate-y-1/2 rounded"
          style={{
            width: `${String(actualPct)}%`,
            background: `var(${colorVar})`,
            transition: 'width 900ms cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        />
        <div
          className="absolute -top-1 -bottom-1 w-0.5 rounded-full bg-[var(--color-text-primary)]"
          style={{
            left: `${String(tickPct)}%`,
            opacity: animateIn ? 0.8 : 0,
            transition: 'opacity 400ms ease-out',
            transitionDelay: animateIn ? '700ms' : '0ms',
          }}
        />
        {onSelect && (
          <button
            type="button"
            data-testid={`idt-bullet-bar-${d.id}`}
            aria-label={t('idtKpi.ptDetail.openLabel', {
              owner: d.id,
              defaultValue: `${d.id} の pt 明細を開く`,
            })}
            onClick={() => {
              onSelect(d.id);
            }}
            className="absolute inset-0 z-[1] cursor-pointer rounded-md transition-shadow duration-150 ease-out hover:shadow-[inset_0_0_0_1px_var(--pt-bar-ring)]"
            style={
              {
                '--pt-bar-ring': `color-mix(in srgb, var(${colorVar}) 55%, transparent)`,
              } as CSSProperties
            }
          />
        )}
        <div
          role="tooltip"
          className={`absolute -top-[60px] left-1/2 -translate-x-1/2 z-10 px-3 py-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] shadow-lg text-xs whitespace-nowrap pointer-events-none transition-opacity duration-200 ease-out ${
            hover ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="text-[var(--color-text-primary)] font-medium tabular-nums">
            {d.actual}
            {d.inProgress > 0 && (
              <span style={{ color: `var(${colorVar})` }}>（{d.inProgress}）</span>
            )}
            {' / '}
            {d.monthlyTarget}
            <span className="text-[var(--color-text-muted)] ml-1.5 font-normal">
              {t('idtKpi.bulletTooltip.actualVsMonthly')}
            </span>
          </div>
          <div className="text-[var(--color-text-muted)] tabular-nums">
            {t('idtKpi.bulletTooltip.todayTarget')}: {d.currentTarget}
          </div>
        </div>
      </div>
      <div
        className="text-right text-xs font-semibold tabular-nums"
        style={{
          color: isAhead ? `var(${colorVar})` : 'var(--color-text-secondary)',
          opacity: animateIn ? 1 : 0,
          transition: 'opacity 250ms ease-out',
        }}
      >
        {displayedPct}%
      </div>
    </div>
  );
}

/** @graph-connects none */
function TeamProgressRow({
  data,
  animateIn,
  breakdown,
  onSelect,
}: {
  data: DesignerKpi[];
  animateIn: boolean;
  breakdown?: { completed: number; inProgress: number };
  onSelect?: () => void;
}) {
  const { t } = useTranslation();
  const [hover, setHover] = useState(false);
  const totalActual = data.reduce((s, d) => s + d.actual, 0);
  const totalCurrent = data.reduce((s, d) => s + d.currentTarget, 0);
  const totalMonthly = data.reduce((s, d) => s + d.monthlyTarget, 0);
  // BulletRow と同様: 実績超過時は max(実績, 目標) を全幅基準にして総目標 bar を比例縮小
  const denom = Math.max(totalActual, totalMonthly) || 1;
  const targetBarPct = (totalMonthly / denom) * 100;
  const actualPct = animateIn ? (totalActual / denom) * 100 : 0;
  const tickPct = (totalCurrent / denom) * 100;
  const pct = totalCurrent > 0 ? Math.round((totalActual / totalCurrent) * 100) : 0;
  const displayedPct = useCountUp(pct, animateIn, 1100, 150);

  return (
    <div className="grid grid-cols-[56px_1fr_52px] items-center gap-3">
      <span
        className="inline-flex justify-center px-2.5 py-1 rounded-md text-[11px] font-medium tracking-wide select-none"
        style={{
          background: 'color-mix(in srgb, var(--color-error) 16%, transparent)',
          color: 'var(--color-error)',
        }}
      >
        {t('idtKpi.personalProgress.teamLabel')}
      </span>
      <div
        className="relative h-9 rounded-md"
        style={{ background: 'transparent' }}
        onMouseEnter={() => {
          setHover(true);
        }}
        onMouseLeave={() => {
          setHover(false);
        }}
      >
        {/* 総目標 bar (実績が超過したら比例縮小する)。超過領域は背景なし (カード色) */}
        <div
          className="absolute inset-y-0 left-0 rounded-md"
          style={{
            width: `${String(targetBarPct)}%`,
            background: 'color-mix(in srgb, var(--color-error) 30%, transparent)',
            transition: 'width 1000ms cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        />
        <div
          className="absolute top-1/2 left-0 h-6 -translate-y-1/2 rounded"
          style={{
            width: `${String(actualPct)}%`,
            background: 'var(--color-error)',
            transition: 'width 1000ms cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        />
        <div
          className="absolute -top-1 -bottom-1 w-0.5 rounded-full bg-[var(--color-text-primary)]"
          style={{
            left: `${String(tickPct)}%`,
            opacity: animateIn ? 0.8 : 0,
            transition: 'opacity 400ms ease-out',
            transitionDelay: animateIn ? '800ms' : '0ms',
          }}
        />
        {onSelect && (
          <button
            type="button"
            data-testid="idt-bullet-bar-team"
            aria-label={t('idtKpi.ptDetail.openTeamLabel', 'チーム全体の pt 明細を開く')}
            onClick={onSelect}
            className="absolute inset-0 z-[1] cursor-pointer rounded-md transition-shadow duration-150 ease-out hover:shadow-[inset_0_0_0_1px_var(--pt-bar-ring)]"
            style={
              {
                '--pt-bar-ring': 'color-mix(in srgb, var(--color-error) 55%, transparent)',
              } as CSSProperties
            }
          />
        )}
        <div
          role="tooltip"
          className={`absolute -top-[80px] left-1/2 -translate-x-1/2 z-10 px-3 py-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] shadow-lg text-xs whitespace-nowrap pointer-events-none transition-opacity duration-200 ease-out ${
            hover ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="text-[var(--color-text-primary)] font-medium tabular-nums">
            {Math.round(totalActual)} / {Math.round(totalMonthly)}
            <span className="text-[var(--color-text-muted)] ml-1.5 font-normal">
              {t('idtKpi.bulletTooltip.actualVsMonthly')}
            </span>
          </div>
          {breakdown && (
            <div className="text-[var(--color-text-muted)] tabular-nums">
              {t('idtKpi.bulletTooltip.done', '完了')}: {Math.round(breakdown.completed)} ·{' '}
              {t('idtKpi.bulletTooltip.inProgress', '処理中')}: {Math.round(breakdown.inProgress)}
            </div>
          )}
          <div className="text-[var(--color-text-muted)] tabular-nums">
            {t('idtKpi.bulletTooltip.todayTarget')}: {Math.round(totalCurrent)}
          </div>
        </div>
      </div>
      <span
        className="text-right text-xs font-semibold tabular-nums"
        style={{
          color: 'var(--color-error)',
          opacity: animateIn ? 1 : 0,
          transition: 'opacity 250ms ease-out',
        }}
      >
        {displayedPct}%
      </span>
    </div>
  );
}
